import { useState, useEffect, ReactNode, useRef } from 'react';
import DeleteButton from '../deleteButton/DeleteButton';
import { NumberField } from '../formFields/NumberField';
import SearchBox from '../formFields/searchBox/SearchBox';
import { TextField } from '../formFields/TextField';
import { CurrencyField } from '../formFields/currencyField/CurrencyField';
import { formatStringToUserFullDate } from '../../helpers/dates.helper';
 
export type ColumnConfig = {
  key: string;
  header: string;
  dataType: 'text' | 'number' | 'percentage' | 'date' | 'boolean';
  editConfigs?: {
	type: 'INTEGER' | 'TEXT' | 'SEARCHBOX' | 'CURRENCY' | 'DATE',
	data?: any[],
	display?: string,
	search?: string,
	key?: string,
  }
  filterable?: boolean;
  filterBy?: 'VALUE' | 'FORMATTED' | 'BOTH';
  filterDefault?: string;
  filterOptions?: Array<{ value: any; label: string }>;
  sortable?: boolean;
  horizontalAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  width?: number;
  formatter?: (value: any) => string | ReactNode;
};
 
export type CommandButton = {
  type: 'command';
  label: string;
  onClick: () => void;
  disabled?: boolean;
}
 
export type TitleComponent = {
  type: 'title';
  title: string;
  count?: boolean;
}
 
export type ToolbarComponent = CommandButton | TitleComponent
 
export type GridProps = {
  data: any[];
  columns: ColumnConfig[];
  pageSize?: number;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  rowActions?: Array<{
	label: string;
	icon?: string;
	preset?: string;
	config?: any;
	action: (selectedRows: any[]) => void;
  }>;
  enablePagination?: boolean;
  actionsColumnWidth?: number | string;
  toolbarComponents?: ToolbarComponent[];
  // createButton?: {
  //   label: string;
  //   onClick: () => void;
  //   disabled?: boolean;
  // };
  checkbox?: boolean;
};
 
const setDefaultFilters = (columns: ColumnConfig[]) => {
  let defaults: any = {}
  if (!columns?.length) return defaults

  columns.forEach(c => {
	if (c.filterDefault) {
	  if (c.dataType === 'date') {
		defaults[c.key] = { from: '', to: '' }
	  } else if (c.filterOptions) {
		// Si hay filterOptions y filterDefault, inicializar con el valor por defecto
		// Convertir a string para comparar con los valores de las opciones
		const defaultValue = String(c.filterDefault);
		// Verificar que el valor existe en las opciones
		const optionExists = c.filterOptions.some(opt => String(opt.value) === defaultValue);
		defaults[c.key] = optionExists ? [defaultValue] : [];
	  } else {
		defaults[c.key] = c.filterDefault
	  }
	} else if (c.dataType === 'date' && c.filterable) {
	  defaults[c.key] = { from: '', to: '' }
	} else if (c.filterOptions && c.filterable) {
	  defaults[c.key] = []
	}
  })
   
  return defaults
}

// Componente dropdown compacto para filtros
type FilterDropdownProps = {
  options: Array<{ value: any; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
};

const FilterDropdown = ({ options, selectedValues, onChange, placeholder = 'Todo' }: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);

  // Calcular posición del dropdown cuando se abre
  useEffect(() => {
	if (isOpen && displayRef.current) {
	  const rect = displayRef.current.getBoundingClientRect();
	  setDropdownPosition({
		top: rect.bottom + window.scrollY,
		left: rect.left + window.scrollX,
		width: rect.width
	  });
	}
  }, [isOpen]);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
	const handleClickOutside = (event: MouseEvent) => {
	  if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
		setIsOpen(false);
	  }
	};

	document.addEventListener('mousedown', handleClickOutside);
	return () => {
	  document.removeEventListener('mousedown', handleClickOutside);
	};
  }, []);

  const handleOptionToggle = (optionValue: string) => {
	const valueStr = String(optionValue);
	const isSelected = selectedValues.includes(valueStr);
	 
	if (isSelected) {
	  // Remover de la selección
	  onChange(selectedValues.filter(v => v !== valueStr));
	} else {
	  // Agregar a la selección
	  onChange([...selectedValues, valueStr]);
	}
  };

  const handleSelectAll = () => {
	if (selectedValues.length === options.length) {
	  // Si todos están seleccionados, deseleccionar todos
	  onChange([]);
	} else {
	  // Seleccionar todos
	  onChange(options.map(option => String(option.value)));
	}
  };

  const getDisplayText = () => {
	if (selectedValues.length === 0) return placeholder;
	if (selectedValues.length === 1) {
	  const option = options.find(opt => String(opt.value) === selectedValues[0]);
	  return option?.label || selectedValues[0];
	}
	return `${selectedValues.length} seleccionados`;
  };

  const toggleDropdown = () => {
	setIsOpen(!isOpen);
  };

  return (
	<div className="filter-dropdown-container" ref={containerRef}>
	  <div 
		ref={displayRef}
		className="filter-dropdown-display"
		onClick={toggleDropdown}
		tabIndex={0}
		role="button"
		aria-expanded={isOpen}
		aria-haspopup="listbox"
	  >
		<span className="filter-dropdown-text">{getDisplayText()}</span>
		<div className={`filter-dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</div>
	  </div>
	   
	  {isOpen && (
		<div 
		  className="filter-dropdown-options"
		  style={{
			position: 'fixed',
			top: `${dropdownPosition.top}px`,
			left: `${dropdownPosition.left}px`,
			width: `${dropdownPosition.width}px`
		  }}
		>
		  <div 
			className="filter-dropdown-option filter-dropdown-select-all"
			onClick={handleSelectAll}
		  >
			<input 
			  type="checkbox" 
			  checked={selectedValues.length === options.length && options.length > 0}
			  onChange={() => {}} // Controlled by onClick
			  className="filter-dropdown-checkbox"
			/>
			<span>Todo</span>
		  </div>
		   
		  {options.map((option, idx) => {
			const valueStr = String(option.value);
			const isSelected = selectedValues.includes(valueStr);
			return (
			  <div
				key={idx}
				className={`filter-dropdown-option ${isSelected ? 'selected' : ''}`}
				onClick={() => handleOptionToggle(option.value)}
			  >
				<input 
				  type="checkbox" 
				  checked={isSelected}
				  onChange={() => {}} // Controlled by onClick
				  className="filter-dropdown-checkbox"
				/>
				<span>{option.label}</span>
			  </div>
			);
		  })}
		</div>
	  )}
	</div>
  );
};

const SmartGrid = ({
  data,
  columns,
  pageSize = 10,
  defaultSort,
  rowActions,
  enablePagination = true,
  actionsColumnWidth = '150px',
  // createButton,
  toolbarComponents,
  checkbox = false
}: GridProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [filters, setFilters] = useState<Record<string, string | { from: string; to: string } | string[]>>(setDefaultFilters(columns));
  const [selectedRows, setSelectedRows] = useState<Set<any>>(new Set());
  const [internalData, setInternalData] = useState([...data]);
 
  // Efecto para procesar datos
  useEffect(() => {
	let processedData = [...data];
	// Aplicar filtros
	processedData = processedData.filter(row =>
	  columns.every(col => {
		if (!col.filterable) return true;

		// Filtrado por opciones dropdown (match exacto)
		if (col.filterOptions) {
		  const selectedValues = filters[col.key] as string[] | undefined;
		  if (!selectedValues || selectedValues.length === 0) return true;
		  
		  const rowValue = row[col.key];
		  // Comparación exacta del valor
		  return selectedValues.includes(String(rowValue));
		}

		// Filtrado por rango de fechas
		if (col.dataType === 'date') {
		  const dateFilter = filters[col.key] as { from: string; to: string } | undefined;
		  if (!dateFilter || (!dateFilter.from && !dateFilter.to)) return true;
		  
		  const rowDate = new Date(row[col.key]);
		  if (isNaN(rowDate.getTime())) return false; // Fecha inválida
		  
		  // Normalizar fechas para comparación (solo año, mes, día)
		  const normalizeDate = (date: Date) => {
			const normalized = new Date(date);
			normalized.setHours(0, 0, 0, 0);
			return normalized;
		  };
		  
		  const normalizedRowDate = normalizeDate(rowDate);
		  
		  if (dateFilter.from) {
			const fromDate = normalizeDate(new Date(dateFilter.from));
			if (normalizedRowDate < fromDate) return false;
		  }
		  
		  if (dateFilter.to) {
			const toDate = normalizeDate(new Date(dateFilter.to));
			if (normalizedRowDate > toDate) return false;
		  }
		  
		  return true;
		}

		// Filtrado por texto (comportamiento existente)
		const textFilter = filters[col.key];
		const filterValue = typeof textFilter === 'string' ? textFilter.toLowerCase() : '';
		if (!filterValue) return true;

		// FALTARíA AGREGAR EL "BOTH"
		if (col.filterBy === 'FORMATTED' && col.formatter) {
			let rawValue
			if (col.dataType === 'boolean') {
				rawValue = row[col.key]
			} else {
				rawValue = String(row[col.key])
			}
			debugger
			const cellValue = col.formatter(rawValue);
			if (!cellValue) return false
			return cellValue.toString().toLowerCase().includes(filterValue);
		}
		const cellValue = String(row[col.key]).toLowerCase();
		return cellValue.includes(filterValue);
	  })
	);
 
	// Aplicar ordenamiento
	if (sortConfig) {
	  processedData.sort((a, b) => {
		const aValue = a[sortConfig.key];
		const bValue = b[sortConfig.key];
		 
		if (columns.find(c => c.key === sortConfig.key)?.dataType === 'number') {
		  return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
		}
		 
		return sortConfig.direction === 'asc'
		  ? String(aValue).localeCompare(String(bValue))
		  : String(bValue).localeCompare(String(aValue));
	  });
	}
 
	setInternalData(processedData);
  }, [data, filters, sortConfig, columns]);
 
  // Paginación
  const totalPages = enablePagination ? Math.ceil(internalData.length / pageSize) : 1;
  const paginatedData = enablePagination ? 
	internalData.slice(
		(currentPage - 1) * pageSize,
		currentPage * pageSize
	)
	: internalData;

  const resultsColSpan = columns.length + (checkbox ? 1 : 0) + (rowActions ? 1 : 0);
  const hasInvalidDateRange = columns.some((column) => {
	if (column.dataType !== 'date' || !column.filterable) return false;
	const dateFilter = filters[column.key] as { from: string; to: string } | undefined;
	if (!dateFilter?.from || !dateFilter?.to) return false;
	return new Date(dateFilter.from) > new Date(dateFilter.to);
  });
 
 
  // Handlers
  const handleSort = (key: string) => {
	setSortConfig(prev => ({
	  key,
	  direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
	}));
  };
 
  const handleFilterChange = (key: string, value: string) => {
	setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateFilterChange = (key: string, field: 'from' | 'to', value: string) => {
	setFilters(prev => {
	  const currentFilter = prev[key] as { from: string; to: string } | undefined;
	  return {
		...prev,
		[key]: {
		  from: field === 'from' ? value : (currentFilter?.from || ''),
		  to: field === 'to' ? value : (currentFilter?.to || '')
		}
	  };
	});
  };

  const handleSelectFilterChange = (key: string, selectedValues: string[]) => {
	// Si no hay selección, mostrar todos (filtro vacío)
	setFilters(prev => ({ ...prev, [key]: selectedValues }));
  };
 
  const handleSelectAll = (checked: boolean) => {
	const newSelection = new Set(selectedRows);
	paginatedData.forEach(row => {
	  checked ? newSelection.add(row.id) : newSelection.delete(row.id);
	});
	setSelectedRows(newSelection);
  };
 
  // Render helpers
  const formatValue = (value: any, column: ColumnConfig): string | ReactNode => {
	if (column.formatter) return column.formatter(value);
	 
	switch (column.dataType) {
	  case 'number':
		return new Intl.NumberFormat().format(value);
	  case 'percentage':
		return `${(value * 100).toFixed(2)}%`;
	  case 'date':
		return value ? formatStringToUserFullDate(value) : '---';
		// return value ? new Date(value).toLocaleDateString() : '---';
	  default:
		return value;
	}
  };
 
  return (
	<div className="smart-grid">
	  {/* Controles del grid */}
	  <div className="grid-controls">
		{toolbarComponents && (toolbarComponents.map((component, index) => {
			if (component.type === 'title') 
				return <h3 key={index}>
					{component.title} 
					{component.count && ` (${internalData.length} de ${data.length})`}
				</h3>
			else if (component.type === 'command') return (
			  <button onClick={component.onClick} className="create-button" disabled={component.disabled}>
				{/* <FontAwesomeIcon icon="plus" /> */}
				{component.label}
			  </button>
			)
		  }
		))}
		{enablePagination && 
		<div className="pagination-controls">
		  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
			Anterior
		  </button>
		  <span>Página {currentPage} de {totalPages}</span>
		  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
			Siguiente
		  </button>
		  {/* <select 
			value={pageSize} 
			onChange={e => setCurrentPage(1)}
		  >
			{[10, 20, 50].map(size => (
			  <option key={size} value={size}>{size} por página</option>
			))}
		  </select> */}
		</div>
		}
	  </div>
 
	  {/* Tabla */}
	  <table>
		<thead>
		  <tr>
			{/* Checkbox de selección */}
			{checkbox && <th>
			  <input
				type="checkbox"
				onChange={e => handleSelectAll(e.target.checked)}
				checked={
				  paginatedData.length > 0 &&
				  paginatedData.every(row => selectedRows.has(row.id))
				}
			  />
			</th>}
			 
			{/* Encabezados y filtros */}
			{columns.map(column => (
			  <th key={column.key} style={{ width: column.width }}>
				<div className="header-cell">
				  <span
					className={`sortable ${sortConfig?.key === column.key ? 'active' : ''}`}
					onClick={() => column.sortable && handleSort(column.key)}
				  >
					{column.header}
					{sortConfig?.key === column.key && (
					  <span className={`sort-arrow ${sortConfig.direction}`}>
						{sortConfig.direction === 'asc' ? '↑' : '↓'}
					  </span>
					)}
				  </span>
				   
				  {column.filterable && (
					column.dataType === 'date' ? (
					  <div className="date-filter-container">
						<input
						  type="date"
						  placeholder="Desde"
						  value={(filters[column.key] as { from: string; to: string })?.from || ''}
						  onChange={e => handleDateFilterChange(column.key, 'from', e.target.value)}
						  className="filter-input date-filter"
						/>
						<span className="date-filter-separator">-</span>
						<input
						  type="date"
						  placeholder="Hasta"
						  value={(filters[column.key] as { from: string; to: string })?.to || ''}
						  onChange={e => handleDateFilterChange(column.key, 'to', e.target.value)}
						  className="filter-input date-filter"
						/>
					  </div>
					) : column.filterOptions ? (
					  <FilterDropdown
						options={column.filterOptions}
						selectedValues={(filters[column.key] as string[]) || []}
						onChange={(values) => handleSelectFilterChange(column.key, values)}
						placeholder="Todo"
					  />
					) : (
					  <input
						type="text"
						placeholder={`Filtrar ${column.header}`}
						value={(typeof filters[column.key] === 'string' ? filters[column.key] : (column.filterDefault || '')) as string}
						onChange={e => handleFilterChange(column.key, e.target.value)}
						className="filter-input"
					  />
					)
				  )}
				</div>
			  </th>
			))}
			 
			{/* Acciones */}
			{rowActions && 
				<th style={{ width: actionsColumnWidth }}>
					<div className="header-cell" style={{ justifyContent: 'flex-start' }}>
						Acciones
					</div>
				</th>    
			}
		  </tr>
		</thead>
 
		<tbody>
		  {paginatedData.length === 0 && (
			<tr>
			  <td colSpan={resultsColSpan} className={`empty-results ${hasInvalidDateRange ? 'error' : ''}`}>
				{hasInvalidDateRange
					? 'La fecha inicial debe ser menor o igual a la fecha final'
					: 'No hay data para mostrar con esos filtros'}
			  </td>
			</tr>
		  )}
		  {paginatedData.map(row => (
			<tr key={row.id}>
			  {/* Checkbox de selección */}
			  {checkbox && <td>
				<input
				  type="checkbox"
				  checked={selectedRows.has(row.id)}
				  onChange={e => {
					const newSelection = new Set(selectedRows);
					e.target.checked 
					  ? newSelection.add(row.id) 
					  : newSelection.delete(row.id);
					setSelectedRows(newSelection);
				  }}
				/>
			  </td>}
 
			  {/* Celdas de datos */}
			  {columns.map(column => (
				<td 
				  key={column.key}
				  className={`
					${column.horizontalAlign || 'left'}
					vertical-${column.verticalAlign || 'middle'}
					${column.dataType}
				  `}
				>
				  {column.editConfigs !== undefined ? 
					<>
					  {column.editConfigs.type === 'SEARCHBOX' && <SearchBox 
						  value={row[column.key]}
						  key={column.key}
						  onSelect={(value: any) => {}}
						  data={column?.editConfigs?.data || []}
						  displayKey={column?.editConfigs?.display || 'name'}
						  idKey={column?.editConfigs?.key || 'id'}
						  searchKey={column?.editConfigs?.search || 'name'}
					  />}
					  {column.editConfigs.type === 'INTEGER' && <NumberField
						value={row[column.key]}
						onChange={() => {}}
					  />}
					  {column.editConfigs.type === 'TEXT' && <TextField
						value={row[column.key]}
						onChange={() => {}}
					  />}
					  {column.editConfigs.type === 'CURRENCY' && <CurrencyField
						value={row[column.key]}
						onChange={() => {}}
					  />}
					</>
				  : 
					  formatValue(row[column.key], column)
				  }
				</td>
			  ))}
 
			  {/* Acciones por fila */}
			  {rowActions && (
				<td className="actions" style={{ justifyContent: 'flex-start' }}>
				  {rowActions.map(action => (
					  action.preset === 'delete' ?
					// {action.preset === 'delete' ? 
					  // <h1>pepe</h1>
					  // : (<h1>pepe</h1>)
					  <DeleteButton
						key={action.label}
						itemId={row.id}
						itemName={row[action.config?.campoLeyenda || 'name']}
						apiEndpoint={action.config?.apiEndpoint}
						entityName={action.config?.entityName}
						onDelete={() => action.action([row])}
					  />
					:
					  <button
						key={action.label}
						onClick={() => action.action([row])}
						className="action-button"
					  >
						{action.icon && <i>{action.icon}</i>}
						{action.label}
					  </button>
					// )}
					// }
			  ))}
				</td>
			  )}
			</tr>
		  ))}
		</tbody>
	  </table>
 
	  {/* Acciones masivas */}
	  {rowActions && selectedRows.size > 0 && (
		<div className="bulk-actions">
		  {rowActions.map(action => (
			<button
			  key={action.label}
			  onClick={() => action.action(Array.from(selectedRows))}
			  className="bulk-action-button"
			>
			  {action.label} ({selectedRows.size} seleccionados)
			</button>
		  ))}
		</div>
	  )}
 
	  <style>{`
		.smart-grid {
			font-family: Arial, sans-serif;
			border: 1px solid #ddd;
			border-radius: 4px;
			overflow-x: auto;
			max-height: ${enablePagination ? 'auto' : '600px'};
			overflow-y: ${enablePagination ? 'hidden' : 'auto'};
			position: relative;
		}
 
		.grid-controls {
		  display: flex;
		  justify-content: space-between;
		  padding: 12px;
		  align-items: center;
		  border-bottom: 1px solid #ddd;
		}
 
		 .create-button {
		  background: #4CAF50;
		  color: white;
		  padding: 8px 16px;
		  border: none;
		  border-radius: 4px;
		  cursor: pointer;
		}
 
		.actions {
		  justify-content: flex-start !important;
		}
 
		.pagination-controls {
			display: flex;
			gap: 8px;
			align-items: center;
		}
		   
		table {
		  width: 100%;
		  border-collapse: collapse;
		}
 
		th, td {
		  padding: 12px;
		  border-bottom: 1px solid #ddd;
		  text-align: left;
		}
 
		th {
		  background-color: #f5f5f5;
		  position: sticky;
		  top: 0;
		  overflow: visible;
		}

		.header-cell {
		  display: flex;
		  flex-direction: column;
		  gap: 4px;
		  overflow: visible;
		  position: relative;
		}
 
		.sortable {
		  cursor: pointer;
		  user-select: none;
		}
 
		.sortable:hover {
		  color: #007bff;
		}
 
		.sortable.active {
		  color: #0056b3;
		}
 
		.sort-arrow {
		  margin-left: 4px;
		}
 
		.sort-arrow.desc {
		  transform: rotate(180deg);
		}
 
		.filter-input {
		  padding: 4px;
		  border: 1px solid #ddd;
		  border-radius: 4px;
		  font-size: 0.9em;
		}

		.filter-dropdown-container {
		  position: relative;
		  width: 100%;
		}

		.filter-dropdown-display {
		  width: 100%;
		  padding: 4px 24px 4px 8px;
		  border: 1px solid #ddd;
		  border-radius: 4px;
		  font-size: 0.9em;
		  background-color: white;
		  color: #4a5568;
		  cursor: pointer;
		  transition: border-color 0.2s ease;
		  display: flex;
		  justify-content: space-between;
		  align-items: center;
		  position: relative;
		  min-height: 28px;
		}

		.filter-dropdown-display:hover {
		  border-color: #cbd5e0;
		}

		.filter-dropdown-text {
		  flex: 1;
		  overflow: hidden;
		  text-overflow: ellipsis;
		  white-space: nowrap;
		}

		.filter-dropdown-arrow {
		  position: absolute;
		  right: 8px;
		  top: 50%;
		  transform: translateY(-50%);
		  pointer-events: none;
		  color: #718096;
		  font-size: 0.7em;
		  transition: transform 0.2s ease;
		}

		.filter-dropdown-arrow.open {
		  transform: translateY(-50%) rotate(180deg);
		}

		.filter-dropdown-options {
		  position: fixed;
		  background: white;
		  border: 1px solid #ddd;
		  border-radius: 4px;
		  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		  z-index: 9999;
		  overflow-y: auto;
		  max-height: 200px;
		}

		.filter-dropdown-option {
		  padding: 6px 8px;
		  cursor: pointer;
		  display: flex;
		  align-items: center;
		  gap: 6px;
		  transition: background-color 0.2s ease;
		  border-bottom: 1px solid #f7fafc;
		  font-size: 0.9em;
		}

		.filter-dropdown-option:last-child {
		  border-bottom: none;
		}

		.filter-dropdown-option:hover {
		  background-color: #f7fafc;
		}

		.filter-dropdown-option.selected {
		  background-color: #ebf8ff;
		}

		.filter-dropdown-option.filter-dropdown-select-all {
		  font-weight: 600;
		  background-color: #f7fafc;
		  border-bottom: 2px solid #e2e8f0;
		}

		.filter-dropdown-option.filter-dropdown-select-all:hover {
		  background-color: #edf2f7;
		}

		.filter-dropdown-checkbox {
		  margin: 0;
		  cursor: pointer;
		  accent-color: #4299e1;
		  flex-shrink: 0;
		}

		.date-filter-container {
		  display: flex;
		  align-items: center;
		  gap: 4px;
		}

		.date-filter {
		  flex: 1;
		  min-width: 0;
		}

		.date-filter-separator {
		  font-size: 0.9em;
		  color: #666;
		  white-space: nowrap;
		}
 
		.left { text-align: left; }
		.center { text-align: center; }
		.right { text-align: right; }
 
		.vertical-top { vertical-align: top; }
		.vertical-middle { vertical-align: middle; }
		.vertical-bottom { vertical-align: bottom; }
 
		.number, .percentage { font-family: monospace; }
 
		.actions {
		  display: flex;
		  gap: 8px;
		  justify-content: center;
		}
 
		.action-button {
		  padding: 4px 8px;
		  background: #f0f0f0;
		  border: 1px solid #ddd;
		  border-radius: 4px;
		  cursor: pointer;
		}
 
		.bulk-actions {
		  padding: 12px;
		  background: #f8f9fa;
		  border-top: 1px solid #ddd;
		  display: flex;
		  gap: 8px;
		}

		.empty-results {
		  text-align: center;
		  padding: 10px 8px;
		  color: #6b7280;
		}

		.empty-results.error {
		  color: #dc2626;
		  font-weight: 600;
		}
	  `}</style>
	</div>
  );
};
 
export default SmartGrid;